/**
 * Shared utility helpers for Locations and related pages.
 */

/** Compute location health status from alert count. */
export function statusFromAlerts(count) {
  if (count >= 16) return { label: 'Critical', color: '#E74C3C', bg: 'rgba(231,76,60,.12)', glow: 'rgba(231,76,60,.06)' };
  if (count >= 6)  return { label: 'Warning',  color: '#F39C12', bg: 'rgba(243,156,18,.12)', glow: 'rgba(243,156,18,.06)' };
  return { label: 'Healthy', color: '#2ECC71', bg: 'rgba(46,204,113,.10)', glow: 'transparent' };
}

/** Format a number with no decimals. */
export const fmt = n => Number(n).toFixed(0);

/** Format a number as USD with no cents. */
export const fmtDollar = n =>
  '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Relative time label from an ISO timestamp. */
export function relTime(ts) {
  if (!ts) return 'N/A';
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return d + 'd ago';
  if (d < 30) return Math.floor(d / 7) + 'w ago';
  return new Date(ts).toLocaleDateString();
}

/**
 * Filter and sort locations with attached summaries.
 * @param {Array} locations
 * @param {Object} summaries - keyed by location id
 * @param {{ search, filter, alertsOnly, sortBy }} opts
 */
export function filterAndSortLocations(locations, summaries, { search, filter, alertsOnly, sortBy }) {
  let list = locations.map(l => ({ ...l, s: summaries[l.id] || {} }));

  if (search) {
    const q = search.toLowerCase();
    list = list.filter(l =>
      l.name.toLowerCase().includes(q) ||
      (l.city || '').toLowerCase().includes(q) ||
      (l.address || '').toLowerCase().includes(q) ||
      (l.phone || '').includes(q)
    );
  }

  if (filter === 'critical')  list = list.filter(l => (l.s.alert_products || 0) >= 16);
  else if (filter === 'warning') list = list.filter(l => { const a = l.s.alert_products || 0; return a >= 6 && a < 16; });
  else if (filter === 'healthy') list = list.filter(l => (l.s.alert_products || 0) < 6);

  if (alertsOnly) list = list.filter(l => (l.s.alert_products || 0) > 0);

  list.sort((a, b) => {
    switch (sortBy) {
      case 'name':   return a.name.localeCompare(b.name);
      case 'alerts': return (b.s.alert_products || 0) - (a.s.alert_products || 0);
      case 'value':  return Number(b.s.inventory_value || 0) - Number(a.s.inventory_value || 0);
      case 'pos':    return (b.s.recent_pos || 0) - (a.s.recent_pos || 0);
      default: return 0;
    }
  });
  return list;
}

/** Aggregate summary totals across all locations. */
export function aggregateTotals(locations, summaries) {
  const vals = Object.values(summaries);
  return {
    locations: locations.length,
    products: vals.reduce((s, v) => s + (v.total_products || 0), 0),
    alerts:   vals.reduce((s, v) => s + (v.alert_products || 0), 0),
    value:    vals.reduce((s, v) => s + Number(v.inventory_value || 0), 0),
    pos:      vals.reduce((s, v) => s + (v.recent_pos || 0), 0),
  };
}
